// frontend/src/components/dashboard/pos/hooks/usePosData.js
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import menuService from '../../services/menuService';

const usePosData = () => {
  const { t } = useTranslation();
  
  // States
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const timeout = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 10000)
        );
        
        // Use getAllWithCategories instead of separate calls
        const dataPromise = menuService.getAllWithCategories();
        const data = await Promise.race([dataPromise, timeout]);
        
        if (data && Array.isArray(data)) {
          // ✅ FIXED: Extract unique categories with correct field names
          const categoryMap = new Map();
          
          data.forEach(item => {
            const categoryId = item.category_id;
            
            // Only add category if not already exists (first item wins for icon/color)
            if (!categoryMap.has(categoryId)) {
              categoryMap.set(categoryId, {
                id: categoryId,
                name: item.category_name,
                // ✅ FIXED: Use correct field names from your SQL query
                icon: item.icon,                    // From SQL: c.icon
                colorName: item.colorName,          // From SQL: c.color_name as colorName
                colorValue: item.colorValue,        // From SQL: c.color_value as colorValue
                // ✅ BACKUP: Also include original field names as fallback
                color_name: item.color_name,        
                color_value: item.color_value
              });
            }
          });
          
          const extractedCategories = Array.from(categoryMap.values());
          
          // ✅ DEBUG: Log the extracted categories to verify the fix
          console.log('🧪 FIXED Categories extracted:', extractedCategories);
          
          if (extractedCategories.length > 0) {
            const firstCategory = extractedCategories[0];
            console.log('🧪 First category details:', {
              name: firstCategory.name,
              icon: firstCategory.icon,
              colorValue: firstCategory.colorValue,
              colorName: firstCategory.colorName
            });
            
            if (firstCategory.icon && firstCategory.colorValue) {
              console.log('🎉 CATEGORIES EXTRACTION FIX SUCCESSFUL! Icons and colors should now work!');
            } else {
              console.log('❌ Categories still missing data. Raw item sample:', data[0]);
            }
          }
          
          setCategories(extractedCategories);

          // Transform products with variations support
          const transformedProducts = data.map(item => ({
            id: item.item_id,
            name: item.name || 'Unnamed Item',
            price: parseFloat(item.price),
            category: item.category_id,
            category_name: item.category_name,
            image: item.image,
            description: item.description,
            available: item.available === 1,
            variations: item.variations || [] // Include variations
          }));
          
          setProducts(transformedProducts);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching POS data:', err);
        if (err.message === 'Timeout') {
          setError(t('pos.error.timeout'));
        } else if (!isOnline) {
          setError(t('pos.error.offline'));
        } else {
          setError(t('pos.error.loadFailed'));
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [t, isOnline]);

  // Filter products based on selected category and search term
  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category.toString() === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch && product.available !== false;
  });

  return {
    categories,
    products,
    filteredProducts,
    loading,
    error,
    isOnline,
    selectedCategory,
    searchTerm,
    setSelectedCategory,
    setSearchTerm
  };
};

export default usePosData;